import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useStore } from '@/store';
import { useStoreInfo } from '@/lib/store';
import { formatPrice, getCache, setCache, getDomain, calculateCartDiscounts, parseCurrencyAmount, roundToTwo, toCents, fromCents } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import { ShippingLocation, Product } from '@/types';
import CartItem from '@/components/cart/cart-item';
import { CheckoutNoteDialog } from '@/components/cart/checkout-note-dialog';
import { LocationSelector } from '@/components/cart/location-selector';
import { CustomerDetailsForm } from '@/components/cart/customer-details-form';
import { CountryStateSelector } from '@/components/cart/country-state-selector';
import { PaymentMethodDialog } from '@/components/cart/payment-method-dialog';
import { DiscountSummary } from '@/components/cart/discount-summary';
import { Loader2, AlertCircle, X } from 'lucide-react';

declare global {
  interface Window {
    PaystackPop: any;
    MonnifySDK: any;
  }
}

interface OrderResponse {
  status: boolean;
  message: string;
  orderID?: string;
  trackingId?: string;
}

interface LocationsResponse {
  status: boolean;
  message: string;
  shippingLocations: ShippingLocation[];
  vatType: string;
  vatValue: number;
}

const getDefaultPaymentMethod = (storeInfo: any): 'bank' | 'paystack' | 'monnify' | 'stripe' => {
  if (storeInfo?.stripeActive === 1) return 'stripe';
  if (storeInfo?.mb_active === 1) return 'bank';
  if (storeInfo?.pays_active === 1) return 'paystack';
  if (storeInfo?.monify_active === 1) return 'monnify';
  return 'bank'; // fallback
};

export function CartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, removeFromCart, updateCartQuantity, customerDetails, setCustomerDetails, clearCart } = useStore();
  const { storeInfo } = useStoreInfo();
  const [selectedCountry, setSelectedCountry] = useState<string>(getCache('shipping_country') || '');
  const [selectedState, setSelectedState] = useState<string>(getCache('shipping_state') || '');
  const [selectedLocation, setSelectedLocation] = useState<ShippingLocation | null>(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'bank' | 'paystack' | 'monnify' | 'stripe'>(
    getDefaultPaymentMethod(storeInfo)
  );
  const [showCheckoutNote, setShowCheckoutNote] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [allLocations, setAllLocations] = useState<ShippingLocation[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<ShippingLocation[]>([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [orderPaymentReceipt, setOrderPaymentReceipt] = useState<File | null>(null);
  const [shippingPaymentReceipt, setShippingPaymentReceipt] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [soldOutItems, setSoldOutItems] = useState<number[]>([]);
  const [showSoldOutModal, setShowSoldOutModal] = useState(false);
  const [stockLevels, setStockLevels] = useState<Record<number, { total: number; variations: Record<string, number> }>>({});
  const [showStockUpdateModal, setShowStockUpdateModal] = useState(false);
  const [stockUpdates, setStockUpdates] = useState<Array<{ id: number; name: string; oldQty: number; newQty: number; variation?: string }>>([]);
  const [vatType, setVatType] = useState<string>('');
  const [vatValue, setVatValue] = useState<number>(0);

  useEffect(() => {
    setPaymentMethod(getDefaultPaymentMethod(storeInfo));
  }, [storeInfo]);

  useEffect(() => {
    const savedDetails = localStorage.getItem('customerDetails');
    if (savedDetails) {
      setCustomerDetails(JSON.parse(savedDetails));
    }

    if (storeInfo?.checkout_note) {
      setShowCheckoutNote(true);
    }
  }, [setCustomerDetails, storeInfo]);

  useEffect(() => {
    const checkCartItems = async () => {
      const domain = getDomain();
      try {
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/products.php?domain=${domain}`);
        const data = await response.json();

        if (!data.status) return;

        const newSoldOutItems: number[] = [];
        const newStockLevels: Record<number, { total: number; variations: Record<string, number> }> = {};
        const updates: Array<{ id: number; name: string; oldQty: number; newQty: number; variation?: string }> = [];

        cart.forEach(cartItem => {
          const currentProduct = data.products.find((p: Product) => p.id === cartItem.id);

          if (!currentProduct || currentProduct.status !== 1) {
            newSoldOutItems.push(cartItem.id);
            return;
          }

          if (cartItem.selectedVariations) {
            const variationStocks: Record<string, number> = {};
            let allVariationsSoldOut = true;

            cartItem.selectedVariations.forEach(variation => {
              const currentVariation = currentProduct.variations.find(
                v => v.variation === variation.variation
              );
              const availableStock = currentVariation ? parseInt(currentVariation.v_qty) : 0;
              variationStocks[variation.variation] = availableStock;

              if (availableStock > 0) {
                allVariationsSoldOut = false;
              }

              if (availableStock < variation.quantity) {
                updates.push({
                  id: cartItem.id,
                  name: cartItem.produt_name,
                  oldQty: variation.quantity,
                  newQty: availableStock,
                  variation: variation.variation
                });
              }
            });

            if (allVariationsSoldOut) {
              newSoldOutItems.push(cartItem.id);
            }

            newStockLevels[cartItem.id] = {
              total: currentProduct.variations.reduce((sum, v) => sum + parseInt(v.v_qty), 0),
              variations: variationStocks
            };
          } else {
            const availableStock = parseInt(currentProduct.totalStock.toString());
            newStockLevels[cartItem.id] = {
              total: availableStock,
              variations: {}
            };

            if (availableStock === 0) {
              newSoldOutItems.push(cartItem.id);
            } else if (availableStock < cartItem.quantity) {
              updates.push({
                id: cartItem.id,
                name: cartItem.produt_name,
                oldQty: cartItem.quantity,
                newQty: availableStock
              });
            }
          }
        });

        if (updates.length > 0) {
          setStockUpdates(updates);
          setShowStockUpdateModal(true);
        }

        setSoldOutItems(newSoldOutItems);
        setStockLevels(newStockLevels);
      } catch (error) {
        console.error('Error checking cart items:', error);
      }
    };

    const interval = setInterval(checkCartItems, 10000);
    checkCartItems();

    return () => clearInterval(interval);
  }, [cart, setCustomerDetails]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/locations.php?domain=${domain}`);
        const data: LocationsResponse = await response.json();

        if (data.status && data.shippingLocations) {
          setAllLocations(data.shippingLocations);
          setVatType(data.vatType);
          setVatValue(data.vatValue);
        }
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    };

    fetchLocations();
  }, []);

  useEffect(() => {
    setFilteredLocations(filterLocations());
  }, [selectedCountry, selectedState, allLocations]);

  useEffect(() => {
    const cachedCountry = getCache('shipping_country');
    const cachedState = getCache('shipping_state');
    const cachedLocation = getCache('shipping_location');

    if (cachedCountry) {
      setSelectedCountry(cachedCountry);

      if (cachedState) {
        setSelectedState(cachedState);

        if (cachedLocation) {
          const location = allLocations.find(loc =>
            loc.location === cachedLocation &&
            loc.country === cachedCountry &&
            loc.state === cachedState
          );
          if (location) {
            setSelectedLocation(location);
          }
        }
      }
    }
  }, [allLocations]);
  
  const handleStockUpdateConfirm = () => {
    stockUpdates.forEach(update => {
      if (update.variation) {
        handleVariationQuantityChange(update.id, update.variation, update.newQty - update.oldQty);
      } else {
        updateCartQuantity(update.id, update.newQty);
      }
    });
    setShowStockUpdateModal(false);
    setStockUpdates([]);
  };

  const filterLocations = () => {
    if (!allLocations) return [];
    if (selectedCountry && selectedState) {
      return allLocations.filter(location =>
        location.country === selectedCountry &&
        location.state === selectedState
      );
    } else if (selectedCountry) {
      return allLocations.filter(location =>
        location.country === selectedCountry
      );
    }
    return allLocations;
  };

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    setSelectedState('');
    setSelectedLocation(null);
    setCache('shipping_country', country, 30);
    setCache('shipping_state', '', 30);
    setCache('shipping_location', '', 30);
    setShowCountryDropdown(false);
    setCountrySearch('');
    setStateSearch('');
    setLocationSearch('');
  };

  const handleStateChange = (state: string) => {
    setSelectedState(state);
    setSelectedLocation(null);
    setCache('shipping_state', state, 30);
    setCache('shipping_location', '', 30);
    setShowStateDropdown(false);
    setStateSearch('');
    setLocationSearch('');
  };

  const handleLocationSelect = (location: ShippingLocation) => {
    setSelectedLocation(location);
    setCache('shipping_location', location.location, 30);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'order' | 'shipping') => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        return;
      }

      if (type === 'order') {
        setOrderPaymentReceipt(file);
        toast.success('Order payment receipt selected');
      } else {
        setShippingPaymentReceipt(file);
        toast.success('Shipping payment receipt selected');
      }
    }
  };

  const calculateVat = (amount: number): number => {
    if (!vatValue || vatValue === 0) return 0;

    if (vatType === 'percentage') {
      return roundToTwo((amount * vatValue) / 100);
    } else if (vatType === 'fixed') {
      return roundToTwo(vatValue);
    }

    return 0;
  };

  const rawSubtotalCents = cart.reduce((total, item) => {
    if (item.selectedVariations) {
      return total + item.selectedVariations.reduce((varTotal, variation) => {
        let variationData = null as any;
        if (variation.v_id) {
          variationData = item.variations.find(v => v.v_id === variation.v_id);
        }
        if (!variationData) {
          variationData = item.variations.find(v => v.variation === variation.variation);
        }
        const rawVarAmount = variationData
          ? parseCurrencyAmount(variationData.variation_amount)
          : parseCurrencyAmount(variation.variation_amount);
        const amount = rawVarAmount > 0 ? rawVarAmount : parseCurrencyAmount(item.sellingprice);
        return varTotal + toCents(amount) * variation.quantity;
      }, 0);
    }
    const price = parseCurrencyAmount(item.sellingprice);
    return total + toCents(price) * item.quantity;
  }, 0);

  const rawSubtotal = fromCents(rawSubtotalCents);
  const { totalDiscount, discountBreakdown } = calculateCartDiscounts(cart);
  const subtotal = roundToTwo(rawSubtotal - totalDiscount);
  const shippingCost = roundToTwo(parseCurrencyAmount(selectedLocation?.price || 0));
  const vatAmount = calculateVat(subtotal);
  const total = roundToTwo(subtotal + shippingCost + vatAmount);

  const handleQuantityChange = (id: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    updateCartQuantity(id, newQuantity);
  };

  const handleVariationQuantityChange = (itemId: number, variation: string, change: number) => {
    const item = cart.find(i => i.id === itemId);
    if (!item || !item.selectedVariations) return;

    const updatedVariations = item.selectedVariations.map(v => {
      if (v.variation === variation) {
        const varStock = item.variations.find(pv => pv.variation === variation)?.v_qty || '0';
        const newQuantity = Math.max(0, Math.min(parseInt(varStock), v.quantity + change));
        return { ...v, quantity: newQuantity };
      }
      return v;
    }).filter(v => v.quantity > 0);

    if (updatedVariations.length === 0) {
      removeFromCart(itemId);
    } else {
      updateCartQuantity(itemId, 1, updatedVariations);
    }
  };

  const handleRemoveVariation = (itemId: number, variation: string) => {
    const item = cart.find(i => i.id === itemId);
    if (!item || !item.selectedVariations) return;

    const updatedVariations = item.selectedVariations
      .filter(v => v.variation !== variation);

    if (updatedVariations.length === 0) {
      removeFromCart(itemId);
    } else {
      updateCartQuantity(itemId, 1, updatedVariations);
    }
  };

  const handleCustomerDetailsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCustomerDetails({ ...customerDetails, [name]: value });
  };

  const isFormValid = () => {
    if (!selectedCountry || !selectedState || !selectedLocation) return false;
    
    if (!customerDetails?.fullName || !customerDetails?.email || !customerDetails?.phone) return false;

    const [firstName = '', lastName = ''] = (customerDetails?.fullName || '').split(' ');
    if (!firstName.trim() || !lastName.trim()) return false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerDetails?.email || '')) return false;

    if (!customerDetails?.phone.trim()) return false;

    if (selectedLocation && 
        selectedLocation.price > 0 && 
        !customerDetails?.address) {
      return false;
    }

    return true;
  };

  const handleSubmitOrder = async (confirmedPaymentMethod: string, paymentReference?: string) => {
    if (!selectedLocation || !isFormValid()) {
      toast.error('Please fill in all required customer and shipping details.');
      return;
    }

    if (confirmedPaymentMethod === 'bank') {
      if (!orderPaymentReceipt) {
        toast.error('Please upload payment receipt');
        return;
      }
      if (storeInfo?.mb_active === 1 && storeInfo?.sepShipping === 1 && shippingCost > 0 && !shippingPaymentReceipt) {
        toast.error('Please upload shipping payment receipt');
        return;
      }
    }

    setIsSubmitting(true);
    setShowPaymentDialog(false);

    try {
      localStorage.setItem('customerDetails', JSON.stringify(customerDetails));

      const orderItems = cart.map(item => {
        if (item.selectedVariations) {
          return item.selectedVariations.map(variation => ({
            product_id: item.product_id,
            product_name: item.produt_name,
            product_variant: variation.variation,
            qty: variation.quantity,
            variant_id: variation.v_id
          }));
        }
        return [{
          product_id: item.product_id,
          product_name: item.produt_name,
          product_variant: 'Nil',
          qty: item.quantity,
          variant_id: null
        }];
      }).flat();

      const payload: any = {
        domain: getDomain(),
        storeId: storeInfo?.id?.toString() || '',
        items: orderItems,
        country: selectedCountry,
        location: selectedLocation.location,
        fullName: customerDetails.fullName,
        email: customerDetails.email,
        phone: customerDetails.phone,
        address: customerDetails.address || '',
        note: customerDetails.note || '',
        paymentMethod: confirmedPaymentMethod,
        orderAmount: subtotal.toString(),
        shippingAmount: shippingCost.toString(),
        vatAmount: vatAmount.toString(),
      };

      if (paymentReference) {
        payload.paymentReference = paymentReference;
      }

      const toBase64 = (file: File) => new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = error => reject(error);
      });

      if (confirmedPaymentMethod === 'bank' && orderPaymentReceipt) {
        payload.paymentReceipt = await toBase64(orderPaymentReceipt);
        if (shippingPaymentReceipt) {
          payload.shippingPaymentReceipt = await toBase64(shippingPaymentReceipt);
        }
      }

      await submitOrder(payload);

    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error('Failed to submit order. Please try again.');
      setIsSubmitting(false);
    }
  };

  const submitOrder = async (payload: any) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/submitOrder.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded with status ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (data.status && data.orderID && data.trackingId) {
        clearCart();
        navigate('/order-complete', {
          state: {
            orderId: data.orderID,
            trackingId: data.trackingId
          }
        });
        return data;
      } else {
        throw new Error(data.message || 'Failed to place order');
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      let errorMessage = 'Failed to place order. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      setIsSubmitting(false);
      return null;
    }
  };

  const pageTitle = `Shopping Cart${storeInfo?.site_name ? ` - ${storeInfo.site_name}` : ''}`;

  const StockUpdateModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
        <div className="flex items-center mb-4">
          <AlertCircle className="h-6 w-6 text-yellow-500 mr-2" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Stock Level Updates
          </h3>
        </div>
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-300">
            Some items in your cart have reduced stock levels:
          </p>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {stockUpdates.map((update, index) => (
              <div key={index} className="text-sm">
                <p className="font-medium text-gray-900 dark:text-white">{update.name}</p>
                <p className="text-gray-600 dark:text-gray-400">
                  {update.variation ? `${update.variation}: ` : ''}
                  Quantity will be reduced from {update.oldQty} to {update.newQty}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6 flex justify-end space-x-4">
          <Button
            variant="outline"
            onClick={() => setShowStockUpdateModal(false)}
          >
            Review Later
          </Button>
          <Button
            onClick={handleStockUpdateConfirm}
          >
            Update Quantities
          </Button>
        </div>
      </div>
    </div>
  );

  const SoldOutModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Items No Longer Available
            </h3>
          </div>
          <button
            onClick={() => setShowSoldOutModal(false)}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          One or more items in your cart are no longer available. Please review your cart and remove the sold out items to continue.
        </p>
        <Button
          onClick={() => setShowSoldOutModal(false)}
          className="w-full"
        >
          Review Cart
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Review your cart and complete your purchase" />
      </Helmet>

      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Shopping Cart</h1>

          {cart.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
              <p className="text-gray-500 dark:text-gray-400">Your cart is empty</p>
              <Button
                onClick={() => window.history.back()}
                className="mt-4"
              >
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="lg:grid lg:grid-cols-12 lg:gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-7">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {cart.map((item) => (
                      <CartItem
                        key={`${item.id}-${JSON.stringify(item.selectedVariations)}`}
                        item={item}
                        onQuantityChange={handleQuantityChange}
                        onVariationQuantityChange={handleVariationQuantityChange}
                        onRemoveVariation={handleRemoveVariation}
                        onRemoveFromCart={removeFromCart}
                        soldOutItems={soldOutItems}
                        stockLevels={stockLevels}
                        cart={cart}
                        rawSubtotal={rawSubtotal}
                      />
                    ))}
                  </ul>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-5 mt-8 lg:mt-0">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">Order Summary</h2>

                  <CountryStateSelector
                    locations={allLocations}
                    selectedCountry={selectedCountry}
                    selectedState={selectedState}
                    countrySearch={countrySearch}
                    stateSearch={stateSearch}
                    showCountryDropdown={showCountryDropdown}
                    showStateDropdown={showStateDropdown}
                    onCountrySearchChange={setCountrySearch}
                    onStateSearchChange={setStateSearch}
                    onCountrySelect={handleCountryChange}
                    onStateSelect={handleStateChange}
                    onToggleCountryDropdown={() => setShowCountryDropdown(!showCountryDropdown)}
                    onToggleStateDropdown={() => setShowStateDropdown(!showStateDropdown)}
                  />

                  <LocationSelector
                    locations={filteredLocations}
                    selectedLocation={selectedLocation}
                    locationSearch={locationSearch}
                    onLocationSearchChange={setLocationSearch}
                    onLocationSelect={handleLocationSelect}
                    showLocationDropdown={showLocationDropdown}
                    onToggleLocationDropdown={() => setShowLocationDropdown(!showLocationDropdown)}
                    selectedCountry={selectedCountry}
                    selectedState={selectedState}
                  />

                  <CustomerDetailsForm
                    customerDetails={customerDetails}
                    onCustomerDetailsChange={handleCustomerDetailsChange}
                    selectedLocation={!!selectedLocation}
                  />

                  <DiscountSummary
                    discountBreakdown={discountBreakdown}
                    totalDiscount={totalDiscount}
                  />

                  <div className="border-t dark:border-gray-700 pt-4">
                    <div className="flex justify-between text-base">
                      <span className="dark:text-gray-300">Items Total</span>
                      <span className="dark:text-white">{formatPrice(rawSubtotal)}</span>
                    </div>
                    
                    {totalDiscount > 0 && (
                      <div className="mt-2 space-y-2">
                        <div className="flex justify-between text-base">
                          <span className="text-green-600 dark:text-green-400">Total Discounts</span>
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            -{formatPrice(totalDiscount)}
                          </span>
                        </div>
                        
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                          <h4 className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                            Discount Details:
                          </h4>
                          <div className="space-y-1">
                            {discountBreakdown.map((discount, index) => (
                              <div key={index} className="text-xs text-green-700 dark:text-green-400">
                                <span className="font-medium">{discount.productName}</span>
                                <span className="ml-2">
                                  ({discount.quantity} items × {discount.discountType === 'percentage' 
                                    ? `${discount.discountValue}% off`
                                    : `${formatPrice(discount.discountValue)} off`}) = 
                                  <span className="font-medium ml-1">
                                    -{formatPrice(discount.discountAmount)}
                                  </span>
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex justify-between text-base font-medium">
                          <span className="dark:text-gray-300">Subtotal (after discounts)</span>
                          <span className="dark:text-white">{formatPrice(subtotal)}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-base mt-2">
                      <span className="dark:text-gray-300">Shipping</span>
                      <span className="dark:text-white">{formatPrice(shippingCost, storeInfo?.currenCy)}</span>
                    </div>
                    {vatValue > 0 && (
                      <div className="flex justify-between text-base mt-2">
                        <span className="dark:text-gray-300">
                          VAT {vatType === 'percentage' ? `(${vatValue}%)` : ''}
                        </span>
                        <span className="dark:text-white">{formatPrice(vatAmount, storeInfo?.currenCy)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-medium mt-4">
                      <span className="dark:text-gray-300">Total</span>
                      <span className="dark:text-white">{formatPrice(total, storeInfo?.currenCy)}</span>
                    </div>
                  </div>
                      {totalDiscount > 0 && (
                        <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                          You saved {formatPrice(totalDiscount)} with discounts!
                        </p>
                      )}

                  <Button
                    onClick={() => setShowPaymentDialog(true)}
                    className="w-full h-14 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting || soldOutItems.length > 0 || !isFormValid()}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                        Processing...
                      </div>
                    ) : soldOutItems.length > 0 ? (
                      'Remove Sold Out Items to Continue'
                    ) : !isFormValid() ? (
                      'Please Fill All Required Fields'
                    ) : (
                      'Proceed to Payment'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {storeInfo?.checkout_note && (
        <CheckoutNoteDialog
          open={showCheckoutNote}
          onClose={() => setShowCheckoutNote(false)}
          note={storeInfo.checkout_note}
        />
      )}

      <PaymentMethodDialog
        open={showPaymentDialog}
        onClose={() => setShowPaymentDialog(false)}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
        onConfirm={(confirmedMethod, reference) => {
          handleSubmitOrder(confirmedMethod, reference);
        }}
        orderTotal={subtotal}
        rawSubtotal={rawSubtotal}
        shippingTotal={shippingCost}
        vatAmount={vatAmount}
        customerEmail={customerDetails?.email}
        customerName={customerDetails?.fullName}
        storeInfo={storeInfo}
        cart={cart}
        onFileUpload={handleFileUpload}
        orderPaymentReceipt={orderPaymentReceipt}
        shippingPaymentReceipt={shippingPaymentReceipt}
        selectedCountry={selectedCountry}
        selectedState={selectedState}
        selectedLocation={selectedLocation}
      />

      {showStockUpdateModal && <StockUpdateModal />}
      {showSoldOutModal && <SoldOutModal />}
    </>
  );
}